import React from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Divider, Button, Comment as CommentAntd, Form, Input } from 'antd';
import { useAppDispatch } from 'src/hooks/store';

import { User } from 'src/slices/auth';
import { Post } from 'src/slices/post';
import {
  addLikeToComment,
  Comment,
  createNewComment,
  removeLikeOfComment,
} from 'src/slices/comment';

import { CommentsList } from 'src/components/CommentsList';

const { TextArea } = Input;

const useStyles = createUseStyles({
  discussion: {
    backgroundColor: 'white',
    borderRadius: '5px',
    boxShadow: '0 0 5px #ccc',
    padding: '20px',
  },
  discussionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bolder',
  },
  discussionDivider: {
    marginTop: '10px',
    marginBottom: '5px',
  },
  discussionSummary: {
    backgroundColor: '#e2e2e2',
    padding: '10px',
    borderRadius: '5px',
  },
  discussionSummaryTitle: {
    display: 'inline-block',
    fontWeight: 'bolder',
  },
  commentButton: {
    float: 'right',
  },
  reply: {
    '& .ant-comment-inner': {
      padding: '10px 0px 0px',
    },
    '& .ant-form-item': {
      margin: '0px',
      padding: '0 0 10px',
    },
  },
});

const Editor = ({
  onChange,
  onSubmit,
  submitting,
  value,
}: {
  onChange: any;
  onSubmit: any;
  submitting: boolean;
  value: string;
}) => {
  const classes = useStyles();
  return (
    <>
      <Form.Item>
        <TextArea rows={4} onChange={onChange} value={value} />
      </Form.Item>
      <Form.Item>
        <Button
          htmlType="submit"
          loading={submitting}
          onClick={onSubmit}
          type="primary"
          className={classes.commentButton}
        >
          Add Comment
        </Button>
      </Form.Item>
    </>
  );
};

interface ArticleDiscussionProps {
  post?: Post;
  comments: Comment[];
  users: { [userId: string]: User };
  postId: string;
  currentUser: User | null | undefined;
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ArticleDiscussion: React.VFC<
  ArticleDiscussionProps & React.HTMLAttributes<HTMLDivElement>
> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const likeCommentClick = (e: any, comment: Comment) => {
    e.preventDefault();

    if (!comment || !comment.likes) {
      return;
    }

    if (
      props.currentUser &&
      comment?.likes.find((id) => {
        return id === props.currentUser?._id;
      })
    ) {
      dispatch(removeLikeOfComment(comment._id));
    } else {
      dispatch(addLikeToComment(comment._id));
    }
  };

  const getBtnTypeLikeComment = (comment: Comment): boolean => {
    if (!comment || !comment.likes) {
      return false;
    }

    return props.currentUser &&
      comment.likes.find((id) => {
        return id === props.currentUser?._id;
      })
      ? true
      : false;
  };

  const submitComment = () => {
    if (!props.value) {
      return;
    }

    props.setSubmitting(true);

    const newComment = async () => {
      await dispatch(
        createNewComment({
          content: props.value,
          postId: props.postId,
        }),
      );

      props.setSubmitting(false);
      props.setValue('');
    };

    newComment();
  };

  return (
    <div className={classes.discussion}>
      <div className={classes.discussionTitle}>
        {t('article.discussion')}
        <Divider className={classes.discussionDivider} />
      </div>
      {props.post?.summary !== undefined ? (
        <div className={classes.discussionSummary}>
          <div className={classes.discussionSummaryTitle}>
            {t('article.summaryOf')}
          </div>
          {` : ${props.post?.summary}`}
        </div>
      ) : (
        <></>
      )}
      <div className="comments">
        {props.comments.length > 0 && (
          <CommentsList
            comments={props.comments}
            users={props.users}
            likeBtn={likeCommentClick}
            getBtnType={getBtnTypeLikeComment}
          />
        )}
        <CommentAntd
          className={classes.reply}
          content={
            <Editor
              onChange={(e: any) => props.setValue(e.target.value)}
              onSubmit={submitComment}
              submitting={props.submitting}
              value={props.value}
            />
          }
        />
      </div>
    </div>
  );
};
